import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  Car,
  CreditCard,
  Download,
  FileText,
  RefreshCw,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { alertService, clientService, invoiceService, vehicleService } from '../services/api';
import { exportCsv } from '../utils/csv';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const chartColors = ['#14b8a6', '#f59e0b', '#ef4444', '#38bdf8', '#a78bfa', '#22c55e'];

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const statusLabels = {
  open: 'Em aberto',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
};

const severityLabels = {
  low: 'Baixa',
  medium: 'Media',
  high: 'Alta',
  critical: 'Critica',
};

const typeLabels = {
  speed_limit: 'Velocidade',
  geofence_enter: 'Entrada cerca',
  geofence_exit: 'Saida cerca',
  ignition_on: 'Ignicao ligada',
  ignition_off: 'Ignicao desligada',
  low_battery: 'Bateria baixa',
  device_offline: 'Offline',
  sos: 'SOS',
  vibration: 'Vibracao',
  custom: 'Personalizado',
};

const toDateKey = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const parseMoney = (value) => Number(value || 0);

const startOfCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const inRange = (value, start, end) => {
  const date = toDateKey(value);
  if (!date) return false;
  return (!start || date >= start) && (!end || date <= end);
};

const groupSum = (items, keyGetter, valueGetter = () => 1) => {
  const map = new Map();

  items.forEach((item) => {
    const key = keyGetter(item) || 'Nao informado';
    map.set(key, (map.get(key) || 0) + valueGetter(item));
  });

  return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
};

const groupByDay = (items, dateGetter, valueGetter = () => 1) => {
  const map = new Map();

  items.forEach((item) => {
    const key = toDateKey(dateGetter(item));
    if (!key) return;
    map.set(key, (map.get(key) || 0) + valueGetter(item));
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => ({
      date: format(new Date(`${date}T00:00:00`), 'dd/MM'),
      value,
    }));
};

const Reports = () => {
  const [vehicles, setVehicles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(startOfCurrentMonth());
  const [endDate, setEndDate] = useState(today());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const [vehicleResponse, alertResponse, clientResponse, invoiceResponse] = await Promise.all([
        vehicleService.getAll(),
        alertService.getAll({ limit: 500 }),
        clientService.getAll(),
        invoiceService.getAll(),
      ]);

      setVehicles(vehicleResponse.data.data || []);
      setAlerts(alertResponse.data.data || []);
      setClients(clientResponse.data.data || []);
      setInvoices(invoiceResponse.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar relatorios');
    } finally {
      setLoading(false);
    }
  };

  const report = useMemo(() => {
    const filteredAlerts = alerts.filter((alert) => inRange(alert.created_at, startDate, endDate));
    const filteredInvoices = invoices.filter((invoice) => inRange(invoice.due_date, startDate, endDate));

    const activeVehicles = vehicles.filter((vehicle) => vehicle.is_active);
    const blockedVehicles = vehicles.filter((vehicle) => vehicle.is_blocked);
    const movingVehicles = vehicles.filter((vehicle) => vehicle.positions?.[0]?.motion);
    const activeClients = clients.filter((client) => client.status === 'active');

    const paidTotal = filteredInvoices
      .filter((invoice) => invoice.status === 'paid')
      .reduce((sum, invoice) => sum + parseMoney(invoice.amount), 0);
    const openTotal = filteredInvoices
      .filter((invoice) => invoice.status === 'open')
      .reduce((sum, invoice) => sum + parseMoney(invoice.amount), 0);
    const overdueTotal = filteredInvoices
      .filter((invoice) => invoice.status === 'overdue')
      .reduce((sum, invoice) => sum + parseMoney(invoice.amount), 0);

    const fleetStatus = [
      { name: 'Ativos', value: activeVehicles.length },
      { name: 'Bloqueados', value: blockedVehicles.length },
      { name: 'Movimento', value: movingVehicles.length },
      { name: 'Offline', value: vehicles.filter((vehicle) => !vehicle.is_active).length },
    ];

    const invoiceStatus = groupSum(
      filteredInvoices,
      (invoice) => statusLabels[invoice.status] || invoice.status,
      (invoice) => parseMoney(invoice.amount)
    );

    const alertSeverity = groupSum(
      filteredAlerts,
      (alert) => severityLabels[alert.severity] || alert.severity
    );

    const alertTypes = groupSum(
      filteredAlerts,
      (alert) => typeLabels[alert.type] || alert.type
    ).sort((a, b) => b.value - a.value);

    const revenueByDay = groupByDay(
      filteredInvoices.filter((invoice) => invoice.status === 'paid'),
      (invoice) => invoice.paid_date || invoice.due_date,
      (invoice) => parseMoney(invoice.amount)
    );

    return {
      filteredAlerts,
      filteredInvoices,
      paidTotal,
      openTotal,
      overdueTotal,
      totalRevenue: paidTotal + openTotal + overdueTotal,
      activeVehicles,
      blockedVehicles,
      movingVehicles,
      activeClients,
      fleetStatus,
      invoiceStatus,
      alertSeverity,
      alertTypes,
      revenueByDay,
    };
  }, [alerts, clients, endDate, invoices, startDate, vehicles]);

  const exportSummary = () => {
    exportCsv('rcrastro-relatorio-geral.csv', [
      { key: 'metric', label: 'Indicador' },
      { key: 'value', label: 'Valor' },
    ], [
      { metric: 'Periodo inicial', value: startDate },
      { metric: 'Periodo final', value: endDate },
      { metric: 'Veiculos', value: vehicles.length },
      { metric: 'Veiculos ativos', value: report.activeVehicles.length },
      { metric: 'Veiculos bloqueados', value: report.blockedVehicles.length },
      { metric: 'Clientes', value: clients.length },
      { metric: 'Clientes ativos', value: report.activeClients.length },
      { metric: 'Alertas no periodo', value: report.filteredAlerts.length },
      { metric: 'Cobrancas no periodo', value: report.filteredInvoices.length },
      { metric: 'Recebido', value: currency.format(report.paidTotal) },
      { metric: 'Em aberto', value: currency.format(report.openTotal) },
      { metric: 'Vencido', value: currency.format(report.overdueTotal) },
    ]);
    toast.success('Relatorio geral exportado');
  };

  const exportAlerts = () => {
    exportCsv('rcrastro-relatorio-alertas.csv', [
      { key: 'title', label: 'Titulo' },
      { key: 'vehicle', label: 'Veiculo' },
      { key: 'type', label: 'Tipo' },
      { key: 'severity', label: 'Severidade' },
      { key: 'created_at', label: 'Criado em' },
      { key: 'message', label: 'Mensagem' },
    ], report.filteredAlerts.map((alert) => ({
      title: alert.title,
      vehicle: alert.vehicle?.name || '',
      type: typeLabels[alert.type] || alert.type,
      severity: severityLabels[alert.severity] || alert.severity,
      created_at: alert.created_at ? format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm') : '',
      message: alert.message,
    })));
    toast.success('Relatorio de alertas exportado');
  };

  const exportFinancial = () => {
    exportCsv('rcrastro-relatorio-financeiro.csv', [
      { key: 'invoice_number', label: 'Numero' },
      { key: 'client', label: 'Cliente' },
      { key: 'description', label: 'Descricao' },
      { key: 'amount', label: 'Valor' },
      { key: 'status', label: 'Status' },
      { key: 'due_date', label: 'Vencimento' },
      { key: 'paid_date', label: 'Pagamento' },
    ], report.filteredInvoices.map((invoice) => ({
      invoice_number: invoice.invoice_number,
      client: invoice.client?.name || '',
      description: invoice.description,
      amount: currency.format(parseMoney(invoice.amount)),
      status: statusLabels[invoice.status] || invoice.status,
      due_date: invoice.due_date ? format(new Date(`${invoice.due_date}T00:00:00`), 'dd/MM/yyyy') : '',
      paid_date: invoice.paid_date ? format(new Date(`${invoice.paid_date}T00:00:00`), 'dd/MM/yyyy') : '',
    })));
    toast.success('Relatorio financeiro exportado');
  };

  const tooltipStyle = {
    background: '#262626',
    border: '1px solid #525252',
    borderRadius: 8,
    color: '#f1f5f9',
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Relatorios</h1>
              <p className="text-dark-400">Visao gerencial da frota, alertas, clientes e financeiro.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={exportSummary}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Geral
              </button>
              <button
                type="button"
                onClick={loadData}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
            </div>
          </div>

          <div className="card mb-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto_auto] lg:items-end">
              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Inicio</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Fim</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="input-field"
                />
              </div>
              <button
                type="button"
                onClick={exportAlerts}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Alertas CSV
              </button>
              <button
                type="button"
                onClick={exportFinancial}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <CreditCard className="h-4 w-4" />
                Financeiro CSV
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <Car className="h-4 w-4" />
                Frota
              </div>
              <p className="text-3xl font-bold text-white">{vehicles.length}</p>
              <p className="mt-1 text-sm text-success-400">{report.activeVehicles.length} ativos</p>
            </div>
            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <Users className="h-4 w-4" />
                Clientes
              </div>
              <p className="text-3xl font-bold text-white">{clients.length}</p>
              <p className="mt-1 text-sm text-success-400">{report.activeClients.length} ativos</p>
            </div>
            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <AlertTriangle className="h-4 w-4" />
                Alertas
              </div>
              <p className="text-3xl font-bold text-warning-400">{report.filteredAlerts.length}</p>
              <p className="mt-1 text-sm text-dark-400">No periodo selecionado</p>
            </div>
            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <TrendingUp className="h-4 w-4" />
                Recebido
              </div>
              <p className="text-3xl font-bold text-primary-400">{currency.format(report.paidTotal)}</p>
              <p className="mt-1 text-sm text-dark-400">Pago no periodo</p>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <section className="card">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Financeiro por status</h2>
                  <p className="text-sm text-dark-400">Valores das cobrancas filtradas.</p>
                </div>
                <BarChart3 className="h-5 w-5 text-primary-400" />
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.invoiceStatus}>
                    <CartesianGrid stroke="#404040" strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#a3a3a3" fontSize={12} />
                    <YAxis stroke="#a3a3a3" fontSize={12} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => currency.format(Number(value || 0))}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {report.invoiceStatus.map((entry, index) => (
                        <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="card">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Recebimentos por dia</h2>
                  <p className="text-sm text-dark-400">Entrada financeira no periodo.</p>
                </div>
                <Calendar className="h-5 w-5 text-primary-400" />
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={report.revenueByDay}>
                    <CartesianGrid stroke="#404040" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#a3a3a3" fontSize={12} />
                    <YAxis stroke="#a3a3a3" fontSize={12} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value) => currency.format(Number(value || 0))}
                    />
                    <Line type="monotone" dataKey="value" stroke="#14b8a6" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_1fr_1fr]">
            <section className="card">
              <h2 className="mb-4 text-lg font-bold text-white">Status da frota</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={report.fleetStatus} dataKey="value" nameKey="name" outerRadius={88} label>
                      {report.fleetStatus.map((entry, index) => (
                        <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="card">
              <h2 className="mb-4 text-lg font-bold text-white">Alertas por severidade</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.alertSeverity} layout="vertical">
                    <CartesianGrid stroke="#404040" strokeDasharray="3 3" />
                    <XAxis type="number" stroke="#a3a3a3" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="#a3a3a3" fontSize={12} width={70} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="card">
              <div className="mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-400" />
                <h2 className="text-lg font-bold text-white">Tipos de alerta</h2>
              </div>
              <div className="space-y-3">
                {report.alertTypes.length === 0 ? (
                  <div className="py-8 text-center text-dark-400">Sem alertas no periodo</div>
                ) : (
                  report.alertTypes.slice(0, 6).map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between rounded-lg bg-dark-700 p-3">
                      <div className="flex items-center gap-3">
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: chartColors[index % chartColors.length] }}
                        />
                        <span className="text-sm font-medium text-white">{item.name}</span>
                      </div>
                      <span className="badge badge-info">{item.value}</span>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          {loading && (
            <div className="fixed bottom-6 right-6 rounded-lg border border-dark-700 bg-dark-800 px-4 py-3 text-sm text-dark-200 shadow-xl">
              Carregando relatorios...
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Reports;
